import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import { useNavigate } from 'react-router-dom';

const AboutUs = () => {
  const navigate = useNavigate();
  const [hoveredService, setHoveredService] = useState(null);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [activeTab, setActiveTab] = useState('mission');
  const [openFaq, setOpenFaq] = useState(null);
  const [teamSize, setTeamSize] = useState(100);
  const [completionRate, setCompletionRate] = useState(40);
  const [hourlyWage, setHourlyWage] = useState(55);
  const [counterValues, setCounterValues] = useState({});
  const [scrolled, setScrolled] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.98]);

  // Services data
  const services = [
    {
      title: 'Live Webinars',
      description: 'Interactive expert-led training sessions with real-time engagement',
      icon: 'Video',
      gradient: 'from-blue-500 to-indigo-600',
      stats: '10K+ Sessions',
      features: ['Real-time Q&A', 'Expert Speakers', 'HD Recording', 'Breakout Rooms'],
      trending: '+45% growth'
    },
    {
      title: 'On-Demand Library',
      description: 'Access 500+ premium courses anytime, anywhere',
      icon: 'Play',
      gradient: 'from-indigo-500 to-purple-600',
      stats: '500+ Courses',
      features: ['24/7 Access', 'Certificates', 'Mobile App', 'Offline Mode'],
      trending: '+120% content'
    },
    {
      title: 'Compliance Tracking',
      description: 'Monitor team certification progress with advanced analytics',
      icon: 'BarChart',
      gradient: 'from-cyan-500 to-blue-600',
      stats: 'Real-time Analytics',
      features: ['Progress Dashboard', 'Audit Reports', 'Team Insights', 'Auto-Renewals'],
      trending: '98% accuracy'
    },
    {
      title: 'Enterprise Solutions',
      description: 'Custom training programs for organizations of any size',
      icon: 'Award',
      gradient: 'from-purple-500 to-pink-600',
      stats: 'Tailored Programs',
      features: ['Custom Branding', 'Dedicated Support', 'API Access', 'SSO Integration'],
      trending: 'Fortune 500 ready'
    }
  ];

  const features = [
    {
      icon: 'Zap',
      title: 'Quick Deployment',
      description: 'Launch webinars in minutes with one-click setup',
      color: 'from-yellow-400 to-orange-500',
      stat: '5 min setup'
    },
    {
      icon: 'Shield',
      title: 'Secure & Compliant',
      description: 'SOC 2 certified with enterprise-grade security',
      color: 'from-green-400 to-emerald-500',
      stat: 'SOC 2 Type II'
    },
    {
      icon: 'Users',
      title: 'Scalable Platform',
      description: 'From 10 to 10,000 attendees seamlessly',
      color: 'from-blue-400 to-indigo-500',
      stat: '10K concurrent'
    },
    {
      icon: 'TrendingUp',
      title: 'Proven Impact',
      description: '99% client satisfaction with measurable ROI',
      color: 'from-purple-400 to-pink-500',
      stat: '99% satisfaction'
    },
 
  ];

  const stats = [
    { number: 50000, label: 'Active Learners', icon: 'Users', color: 'from-blue-400 to-cyan-500', suffix: '+' },
    { number: 24, label: 'Live Support', icon: 'Headphones', color: 'from-indigo-400 to-blue-500', suffix: '/7' },
    { number: 1000, label: 'Organizations', icon: 'Building', color: 'from-purple-400 to-indigo-500', suffix: '+' },
    { number: 200, label: 'Countries', icon: 'Globe', color: 'from-cyan-400 to-teal-500', suffix: '+' }
  ];

  const testimonials = [
    {
      quote: "Daily Respond transformed how we onboard new hires. Completion rates jumped from 42% to 91% within the first quarter.",
      author: "Sarah Johnson",
      role: "VP of HR",
      company: "TechCorp",
      rating: 5,
      impact: "91% completion rate",
      avatar: "SJ"
    },
    {
      quote: "We've seen a 3× increase in training completion rates since switching. The compliance dashboard gives my team real-time visibility.",
      author: "Michael Chen",
      role: "Training Director",
      company: "Global Manufacturing",
      rating: 5,
      impact: "3x completion rate",
      avatar: "MC"
    },
    {
      quote: "The enterprise tier was exactly what we needed. Custom branding, SSO with Okta, and a dedicated CSM who knows our industry.",
      author: "Anita Puri",
      role: "L&D Manager",
      company: "Regional Hospital",
      rating: 5,
      impact: "Seamless integration",
      avatar: "AP"
    },
    {
      quote: "Setup took less than a day. Our first live webinar had 800 attendees without a single technical hiccup.",
      author: "Tom Reeves",
      role: "COO",
      company: "Financial Services",
      rating: 5,
      impact: "800 attendees",
      avatar: "TR"
    }
  ];

  const teamMembers = [
    { name: 'Sarah Mitchell', role: 'Co-founder & CEO', expertise: 'EdTech Leadership', years: '12+', avatar: 'SM', color: 'from-blue-500 to-indigo-600' },
    { name: 'Raj Patel', role: 'Co-founder & CTO', expertise: 'Scalable Systems', years: '15+', avatar: 'RP', color: 'from-indigo-500 to-purple-600' },
    { name: 'Aisha Okonkwo', role: 'VP of Product', expertise: 'Product Strategy', years: '8+', avatar: 'AO', color: 'from-purple-500 to-pink-600' },
    { name: 'James Thornton', role: 'Head of Learning', expertise: 'Instructional Design', years: '10+', avatar: 'JT', color: 'from-pink-500 to-rose-600' }
  ];
const timelineData = [
  { 
    year: 2022, 
    title: '🚀 The Beginning', 
    subtitle: 'Foundation Year',
    desc: 'Daily Respond was founded by a team of educators and engineers who believed professional learning should be engaging, accessible, and measurable. Launched the beta platform with 10 visionary customers.', 
    color: 'from-blue-500 to-indigo-600',
    metrics: [
      { label: 'Customers', value: '10', icon: 'Users' },
      { label: 'Webinars', value: '50+', icon: 'Video' },
      { label: 'Team Size', value: '5', icon: 'Users' }
    ],
    achievements: ['Beta platform launch', '10 early adopters', '$500K pre-seed']
  },
  { 
    year: 2023, 
    title: '📈 Traction & Validation', 
    subtitle: 'Product-Market Fit',
    desc: 'Rapid adoption across industries. Launched the On-Demand Library with 100+ courses and Compliance Tracking feature. Secured first enterprise clients and expanded team to 20 people.', 
    color: 'from-indigo-500 to-purple-600',
    metrics: [
      { label: 'Customers', value: '150+', icon: 'Users' },
      { label: 'Countries', value: '12', icon: 'Globe' },
      { label: 'Courses', value: '100+', icon: 'BookOpen' }
    ],
    achievements: ['150+ customers', 'On-Demand Library', 'Compliance dashboard', '$2M ARR']
  },
  { 
    year: 2024, 
    title: '🎯 Enterprise Breakthrough', 
    subtitle: 'Scaling Up',
    desc: 'Achieved significant enterprise adoption with Fortune 500 clients. Launched mobile apps, achieved SOC 2 Type II certification, and introduced AI-powered transcription. Team grew to 50 people.', 
    color: 'from-purple-500 to-pink-600',
    metrics: [
      { label: 'Customers', value: '500+', icon: 'Users' },
      { label: 'Learners', value: '25K+', icon: 'Users' },
      { label: 'Mobile Users', value: '10K+', icon: 'Smartphone' }
    ],
    achievements: ['Fortune 500 clients', 'Mobile apps launch', 'SOC 2 certified', 'Series A: $8M']
  },
  { 
    year: 2025, 
    title: '🌍 Global Domination', 
    subtitle: 'Market Leadership',
    desc: 'Expanded to 50+ countries with 2,500+ organizations. Launched enterprise tier with SSO, white-labeling, and dedicated customer success. Introduced AI-powered personalized learning paths.', 
    color: 'from-pink-500 to-rose-600',
    metrics: [
      { label: 'Organizations', value: '2.5K+', icon: 'Building' },
      { label: 'Countries', value: '50+', icon: 'Globe' },
      { label: 'Learners', value: '75K+', icon: 'Users' }
    ],
    achievements: ['2,500+ orgs', 'Enterprise tier', 'AI learning paths', 'Series B: $25M']
  },
  { 
    year: 2026, 
    title: '🤖 AI Revolution', 
    subtitle: 'Innovation Peak',
    desc: 'Transformed professional learning with cutting-edge AI features: automated course summaries, real-time sentiment analysis, and intelligent certification tracking. Crossed 100,000 active learners.', 
    color: 'from-rose-500 to-orange-600',
    metrics: [
      { label: 'Learners', value: '100K+', icon: 'Users' },
      { label: 'Certificates', value: '250K+', icon: 'Award' },
      { label: 'Satisfaction', value: '99%', icon: 'ThumbsUp' }
    ],
    achievements: ['100K+ learners', 'AI-powered platform', 'Global recognition', 'Industry awards']
  },
  { 
    year: 2027, 
    title: '✨ Vision 2030', 
    subtitle: 'The Future of Learning',
    desc: 'Pioneering immersive learning experiences with VR/AR integration, building the world\'s largest professional certification marketplace, and empowering 1 million professionals globally.', 
    color: 'from-orange-500 to-amber-600',
    metrics: [
      { label: 'Target Learners', value: '1M+', icon: 'Users' },
      { label: 'Marketplace', value: 'Launch', icon: 'Store' },
      { label: 'Innovation', value: 'VR/AR', icon: 'Cpu' }
    ],
    achievements: ['VR/AR learning', 'Certification marketplace', 'Global community', 'Impact 1M+ lives'],
    isFuture: true
  }
];

  const faqs = [
    { q: "How quickly can we get up and running?", a: "Most teams are running their first live webinar within 24 hours of signing up. Our onboarding team walks you through setup, and we provide pre-built templates for the most common training scenarios." },
    { q: "What's included in the free trial?", a: "Your 14-day free trial includes unlimited live webinars (up to 100 attendees), access to 50 on-demand courses, compliance tracking for up to 25 team members, and full access to our reporting suite." },
    { q: "Does Daily Respond integrate with our existing HR tools?", a: "Yes. We have native integrations with Workday, BambooHR, ADP, Slack, Microsoft Teams, Zoom, Salesforce, and 150+ more through our open REST API." },
    { q: "How does compliance certification tracking work?", a: "When a learner completes a required course or webinar, a certificate is automatically generated and stored. Managers see real-time dashboards showing who is compliant and upcoming expiry dates." },
    { q: "Is our data secure and where is it stored?", a: "Daily Respond is SOC 2 Type II certified and GDPR, HIPAA, and CCPA compliant. Data is stored in AWS data centers in the US and EU with AES-256 encryption at rest." }
  ];

  // ROI Calculator
  const calculateROI = () => {
    const targetCompletion = 85;
    const extraCompletions = Math.round(teamSize * (targetCompletion - completionRate) / 100);
    const hoursSaved = extraCompletions * 3;
    const savings = hoursSaved * hourlyWage;
    return { extraCompletions, hoursSaved, savings };
  };

  const roi = calculateROI();

  // Counter animation
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target;
            const value = parseInt(target.getAttribute('data-value'));
            const suffix = target.getAttribute('data-suffix') || '';
            let start = 0;
            const increment = value / 50;
            const timer = setInterval(() => {
              start += increment;
              if (start >= value) {
                setCounterValues(prev => ({ ...prev, [target.id]: value + suffix }));
                clearInterval(timer);
              } else {
                setCounterValues(prev => ({ ...prev, [target.id]: Math.floor(start) + suffix }));
              }
            }, 20);
          }
        });
      },
      { threshold: 0.5 }
    );
    document.querySelectorAll('.counter-stat').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen pt-16 sm:pt-12 bg-white overflow-hidden">
      {/* Hero Section - Inspired by HTML */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-950 via-indigo-950 to-blue-950">
        <motion.div style={{ opacity, scale }} className="absolute inset-0">
          {/* Animated Grid Background */}
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.03) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
          
          {/* Gradient Orbs */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
          
          {/* Particles */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(30)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute bg-cyan-400/60 rounded-full"
                style={{
                  width: Math.random() * 4 + 2,
                  height: Math.random() * 4 + 2,
                }}
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: window.innerHeight + Math.random() * 200,
                  opacity: 0
                }}
                animate={{
                  y: -Math.random() * window.innerHeight - 100,
                  x: [null, Math.random() * 200 - 100],
                  opacity: [0, Math.random() * 0.6 + 0.2, 0]
                }}
                transition={{
                  duration: Math.random() * 10 + 8,
                  repeat: Infinity,
                  delay: Math.random() * 5,
                  ease: "linear"
                }}
              />
            ))}
          </div>
        </motion.div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 text-white/90 rounded-full text-sm font-semibold mb-8"
            >
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-2 h-2 bg-green-400 rounded-full"
              />
              Trusted by 1,000+ organizations globally
            </motion.span>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight"
            >
              Empowering Teams Through{' '}
              <span className="block mt-3 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400">
                Professional Learning
              </span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed max-w-4xl mx-auto"
            >
              The world's leading webinar platform for compliance training, certification, and career development — 
              built for organizations that take learning seriously.
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
            >
              <motion.div whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.98 }}>
                <Button
                  size="lg"
                  className="group bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-10 py-5 rounded-full shadow-2xl text-lg font-bold"
                  onClick={() => navigate('/webinars/live')}
                >
                  <Icon name="Rocket" size={22} className="mr-2 group-hover:scale-110 transition-transform" />
                  Explore Platform
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/40 text-white hover:bg-white/10 px-10 py-5 rounded-full text-lg font-bold backdrop-blur-sm"
                  onClick={() => document.getElementById('story')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <Icon name="BookOpen" size={22} className="mr-2" />
                  Learn Our Story
                </Button>
              </motion.div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="grid grid-cols-3 gap-8 max-w-2xl mx-auto"
            >
              {[
                { number: 50000, label: 'Users Trained', icon: 'Users', suffix: 'K+' },
                { number: 1000, label: 'Organizations', icon: 'Building', suffix: '+' },
                { number: 99, label: 'Satisfaction', icon: 'ThumbsUp', suffix: '%' }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -8 }}
                  className="text-center group cursor-pointer"
                >
                  <div className="mb-3 flex justify-center">
                    <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/20 transition-colors">
                      <Icon name={stat.icon} size={24} className="text-cyan-400" />
                    </div>
                  </div>
                  <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 mb-2">
                    {stat.number === 50000 ? '50K+' : stat.number === 1000 ? '1K+' : '99%'}
                  </div>
                  <div className="text-sm text-gray-400 uppercase tracking-wider font-semibold">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 cursor-pointer"
          onClick={() => document.getElementById('story')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <div className="flex flex-col items-center gap-2 text-cyan-400">
            <span className="text-xs uppercase tracking-widest font-semibold">Scroll to explore</span>
            <div className="w-6 h-6 border-r-2 border-b-2 border-cyan-400 transform rotate-45 animate-bounce" />
          </div>
        </motion.div>
      </section>

      

      {/* Story Section */}
      <section id="story" className="py-24 bg-gradient-to-b from-white via-blue-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <Image
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                  alt="Team collaboration"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute bottom-6 left-6 bg-white rounded-xl p-4 shadow-xl flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <Icon name="Trophy" size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Founded 2022</div>
                    <div className="text-xs text-gray-600">Building the future of learning</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">Our Story</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
                Built by Educators, <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">For the Modern Workforce</span>
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Daily Respond was born from a simple frustration: professional training was painful, inaccessible, and rarely stuck. 
                Our founders — educators, engineers, and HR veterans — set out to change that.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Starting in 2022, we built a platform where live webinars, self-paced courses, and compliance tracking live together in 
                one seamless experience. Today we power training programs across 200+ countries.
              </p>
              <div className="grid grid-cols-2 gap-4 pt-4">
                {[
                  { icon: 'Heart', title: 'Customer First', desc: 'Your success drives every decision' },
                  { icon: 'Zap', title: 'Innovation', desc: 'Constantly pushing boundaries' },
                  { icon: 'Shield', title: 'Trust', desc: 'SOC 2 certified. Security built-in' },
                  { icon: 'Globe', title: 'Inclusion', desc: 'Learning has no borders' }
                ].map((value, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ y: -2 }}
                    className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={value.icon} size={18} className="text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">{value.title}</h4>
                      <p className="text-xs text-gray-500">{value.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section - Enhanced with 8 cards */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 rounded-full text-sm font-bold mb-6">
              Why Daily Respond
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Built for <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Modern, Scalable Learning</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need for seamless webinar hosting and training delivery, backed by enterprise-grade infrastructure.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -8 }}
                onHoverStart={() => setHoveredFeature(index)}
                onHoverEnd={() => setHoveredFeature(null)}
                className="relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all border border-gray-100 group cursor-pointer overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                <motion.div 
                  className={`w-14 h-14 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-4 shadow-lg`}
                  animate={{ rotate: hoveredFeature === index ? 360 : 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <Icon name={feature.icon} size={24} className="text-white" />
                </motion.div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{feature.description}</p>
                <span className={`inline-block px-3 py-1 bg-gradient-to-r ${feature.color} text-white rounded-full text-xs font-bold`}>
                  {feature.stat}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-indigo-100 to-blue-100 text-indigo-700 rounded-full text-sm font-bold mb-6">
              Platform Features
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Complete Training <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">Solutions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From live webinars to on-demand courses — everything you need in one powerful platform.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                onHoverStart={() => setHoveredService(index)}
                onHoverEnd={() => setHoveredService(null)}
                className="relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all border border-gray-100 overflow-hidden"
              >
                <motion.div
                  animate={{ scale: hoveredService === index ? 2 : 1, opacity: hoveredService === index ? 0.1 : 0 }}
                  className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${service.gradient} rounded-full -mr-16 -mt-16`}
                />
                <div className="relative z-10">
                  <motion.div 
                    className={`w-14 h-14 bg-gradient-to-r ${service.gradient} rounded-xl flex items-center justify-center mb-4 shadow-lg`}
                    animate={{ rotate: hoveredService === index ? 360 : 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Icon name={service.icon} size={24} className="text-white" />
                  </motion.div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {service.features.slice(0, 3).map((feature, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                        {feature}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`inline-block px-3 py-1 bg-gradient-to-r ${service.gradient} text-white rounded-full text-xs font-bold`}>
                      {service.stats}
                    </span>
                    <span className="text-xs text-green-600 font-semibold">{service.trending}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    {/* Timeline Section - Starting from 2022 */}
<section className="py-24 bg-gradient-to-br from-blue-950 via-indigo-950 to-blue-950 text-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="text-center mb-16"
    >
      <span className="inline-block px-5 py-2 bg-white/10 backdrop-blur-sm text-white/80 rounded-full text-sm font-bold mb-6 border border-white/20">
        Our Journey
      </span>
      <h2 className="text-4xl md:text-5xl font-black mb-4">From Startup to Global Leader</h2>
      <p className="text-xl text-blue-200 max-w-3xl mx-auto">
        Six years of building, learning, and growing with our customers — transforming professional learning worldwide.
      </p>
    </motion.div>

    <div className="relative">
      {/* Timeline Line */}
      <div className="absolute left-8 lg:left-1/2 transform lg:-translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-blue-500 via-indigo-500 to-purple-500 hidden lg:block" />
      
      <div className="space-y-12">
        {timelineData.map((item, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.1, duration: 0.6 }}
            viewport={{ once: true }}
            className={`relative flex flex-col lg:flex-row gap-6 ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
          >
            {/* Timeline Dot with Year */}
            <div className="lg:w-5/12" />
            <motion.div 
              whileHover={{ scale: 1.1 }}
              className={`absolute left-8 lg:left-1/2 transform lg:-translate-x-1/2 -translate-x-1/2 w-14 h-14 bg-gradient-to-r ${item.color} rounded-full flex items-center justify-center z-10 shadow-xl ${item.isFuture ? 'animate-pulse' : ''}`}
            >
              <span className="text-white font-bold text-sm">{item.year}</span>
            </motion.div>
            
            {/* Content Card */}
            <div className="lg:w-5/12 pl-14 lg:pl-0">
              <motion.div
                whileHover={{ y: -5, scale: 1.02 }}
                className={`bg-white/10 backdrop-blur-sm rounded-2xl p-6 border ${item.isFuture ? 'border-cyan-400/50' : 'border-white/20'} hover:bg-white/15 transition-all duration-300`}
              >
                {/* Year for Mobile */}
                <div className="lg:hidden flex items-center gap-2 mb-3">
                  <div className={`w-10 h-10 bg-gradient-to-r ${item.color} rounded-full flex items-center justify-center`}>
                    <span className="text-white font-bold text-xs">{item.year}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{item.title}</h3>
                    {item.subtitle && <p className="text-xs text-cyan-400">{item.subtitle}</p>}
                  </div>
                </div>
                
                {/* Desktop View */}
                <div className="hidden lg:block">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${item.isFuture ? 'bg-cyan-500/20 text-cyan-300' : 'bg-white/10 text-gray-300'}`}>
                      {item.subtitle || (item.year === 2027 ? 'Vision 2030' : 'Milestone')}
                    </span>
                    {item.isFuture && (
                      <span className="text-xs bg-gradient-to-r from-cyan-500 to-blue-500 px-2 py-1 rounded-full">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                </div>
                
                {/* Description */}
                <p className="text-blue-200 text-sm leading-relaxed mb-4">{item.desc}</p>
                
                {/* Metrics Grid */}
                {item.metrics && (
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {item.metrics.map((metric, i) => (
                      <div key={i} className="bg-white/5 rounded-lg p-2 text-center">
                        <div className="flex justify-center mb-1">
                          <Icon name={metric.icon} size={14} className="text-cyan-400" />
                        </div>
                        <div className="text-sm font-bold text-white">{metric.value}</div>
                        <div className="text-xs text-blue-300">{metric.label}</div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Achievements Tags */}
                <div className="flex flex-wrap gap-2">
                  {item.achievements.map((achievement, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 + i * 0.05 }}
                      className={`text-xs px-2 py-1 rounded-full ${
                        item.isFuture 
                          ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                          : 'bg-white/10 text-blue-200'
                      }`}
                    >
                      {achievement}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
    
    {/* Timeline Summary Stats */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8 }}
      viewport={{ once: true }}
      className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4"
    >
      {[
        { label: 'Years Active', value: '6+', icon: 'Calendar', color: 'from-blue-500 to-indigo-500' },
        { label: 'Customers', value: '5,000+', icon: 'Users', color: 'from-indigo-500 to-purple-500' },
        { label: 'Learners', value: '100K+', icon: 'Users', color: 'from-purple-500 to-pink-500' },
        { label: 'Countries', value: '50+', icon: 'Globe', color: 'from-pink-500 to-rose-500' }
      ].map((stat, idx) => (
        <motion.div
          key={idx}
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1 + idx * 0.1 }}
          whileHover={{ scale: 1.05, y: -5 }}
          className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20"
        >
          <div className={`w-10 h-10 bg-gradient-to-r ${stat.color} rounded-lg flex items-center justify-center mx-auto mb-2`}>
            <Icon name={stat.icon} size={18} className="text-white" />
          </div>
          <div className="text-xl md:text-2xl font-bold text-white">{stat.value}</div>
          <div className="text-xs text-gray-400">{stat.label}</div>
        </motion.div>
      ))}
    </motion.div>
  </div>
</section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-700 rounded-full text-sm font-bold mb-6">
              Testimonials
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Loved by <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-yellow-600">Thousands of Teams</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't take our word for it — hear from the organizations transforming their training.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="flex gap-0.5 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Icon key={i} name="Star" size={16} className="text-yellow-500 fill-current" />
                  ))}
                </div>
                <div className="text-4xl text-blue-500 opacity-30 mb-2">"</div>
                <p className="text-gray-700 text-sm mb-4 leading-relaxed">{testimonial.quote}</p>
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">{testimonial.author}</div>
                    <div className="text-xs text-gray-500">{testimonial.role}, {testimonial.company}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-4">Numbers That Speak for Themselves</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">The scale of Daily Respond's impact on professional learning worldwide.</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { number: '50K+', label: 'Active Learners', icon: 'Users' },
              { number: '1K+', label: 'Organizations', icon: 'Building' },
              { number: '200+', label: 'Countries', icon: 'Globe' },
              { number: '2.4M', label: 'Certificates', icon: 'Award' },
              { number: '24/7', label: 'Live Support', icon: 'Headphones' },
              { number: '99%', label: 'Satisfaction', icon: 'ThumbsUp' }
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20"
              >
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Icon name={stat.icon} size={24} className="text-white" />
                </div>
                <div className="text-2xl md:text-3xl font-black mb-1">{stat.number}</div>
                <div className="text-xs text-blue-200">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

     

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 rounded-full text-sm font-bold mb-6">
              FAQ
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Common Questions</h2>
            <p className="text-gray-600">Everything you need to know about getting started with Daily Respond.</p>
          </motion.div>

          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left font-medium text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  {faq.q}
                  <Icon name="ChevronDown" size={20} className={`transform transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {openFaq === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-6 pb-4 text-gray-600 text-sm leading-relaxed"
                    >
                      {faq.a}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle, white 2px, transparent 2px)`,
          backgroundSize: '50px 50px'
        }} />
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-5xl mb-4">🚀</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Ready to Transform Your Training?
            </h2>
            <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
              Join 1,000+ organizations delivering world-class learning with Daily Respond. 
              Start your free 14-day trial today — no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-full font-bold text-lg shadow-xl"
                onClick={() => navigate('/register')}
              >
                Start Free Trial →
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg"
                onClick={() => navigate('/contact')}
              >
                Talk to Sales
              </Button>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-white/80 text-sm">
              <div className="flex items-center gap-2"><Icon name="Shield" size={16} /> SOC 2 Certified</div>
              <div className="flex items-center gap-2"><Icon name="Clock" size={16} /> 24/7 Support</div>
              <div className="flex items-center gap-2"><Icon name="Lock" size={16} /> GDPR Ready</div>
              <div className="flex items-center gap-2"><Icon name="CreditCard" size={16} /> No Credit Card</div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;